<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { router } from '@inertiajs/vue3'
import Dialog from 'primevue/dialog'
import Select from 'primevue/select'
import InputNumber from 'primevue/inputnumber'
import InputText from 'primevue/inputtext'
import Textarea from 'primevue/textarea'
import { notify } from '@kyvg/vue3-notification'
import { useFormatCurrency } from '@/Composables/useFormatCurrency'

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faBoxOpen, faPrint } from '@fal'
import { faThumbtack } from '@fas'
import { library } from '@fortawesome/fontawesome-svg-core'
import Button from '../Utils/Button.vue'
import InformationIcon from '../Utils/InformationIcon.vue'
library.add(faBoxOpen, faPrint, faThumbtack)

const props = defineProps<{
    visible: boolean
    seng_products: { id: number; name: string; code: string; is_pinned: boolean; price: number }[]
    customers: { id: number; name: string; orders_count: number }[]
    flexo_product: { id: number; name: string; price: number } | null
    flexo_customer_prices: Record<number, number>
    default_product_id: number | null
}>()

const emit = defineEmits<{
    'update:visible': [value: boolean]
}>()

const selectedType = ref<'seng' | 'flexo'>('seng')
const productId    = ref<number | null>(props.default_product_id)
const customerId   = ref<number | null>(null)
const quantity     = ref(1)
const reference    = ref('')
const fileMerek    = ref('')
const note         = ref('')
const dimWidth     = ref<number | null>(null)
const dimHeight    = ref<number | null>(null)
const isSubmitting = ref(false)

watch(() => props.visible, (val) => {
    if (val) {
        selectedType.value = 'seng'
        productId.value    = props.default_product_id
        customerId.value   = null
        quantity.value     = 1
        reference.value    = ''
        fileMerek.value        = ''
        note.value         = ''
        dimWidth.value     = null
        dimHeight.value    = null
    }
})

const flexoUnitPrice = computed(() => {
    if (!props.flexo_product) return 0
    if (customerId.value && props.flexo_customer_prices[customerId.value]) {
        return props.flexo_customer_prices[customerId.value]
    }
    return props.flexo_product.price
})

const flexoTotal = computed(() => {
    if (!dimWidth.value || !dimHeight.value || !quantity.value) return 0
    return quantity.value * dimWidth.value * dimHeight.value * flexoUnitPrice.value
})

const sengTotal = computed(() => {
    if (!productId.value) return 0
    const p = props.seng_products.find(p => p.id === productId.value)
    return p ? quantity.value * p.price : 0
})

const close = () => emit('update:visible', false)

const submit = () => {
    if (selectedType.value === 'seng' && !productId.value) {
        notify({ title: 'Error', text: 'Pilih produk Seng terlebih dahulu', type: 'error' })
        return
    }
    if (selectedType.value === 'flexo' && (!dimWidth.value || !dimHeight.value)) {
        notify({ title: 'Error', text: 'Masukkan dimensi produk Flexo', type: 'error' })
        return
    }

    isSubmitting.value = true

    const payload: Record<string, unknown> = {
        type:       selectedType.value,
        customer_id: customerId.value ?? undefined,
        quantity:   quantity.value,
        reference:  reference.value || undefined,
        file_merek: fileMerek.value || undefined,
        note:       note.value || undefined,
    }

    if (selectedType.value === 'seng') {
        payload.product_id = productId.value
    } else {
        payload.dimension_width  = dimWidth.value
        payload.dimension_height = dimHeight.value
    }

    router.post(route('casheer.orders.store'), payload, {
        onSuccess: () => {
            close()
            notify({ title: 'Berhasil', text: 'Order berhasil dibuat', type: 'success' })
        },
        onError: () => {
            notify({ title: 'Oops!', text: 'Gagal membuat order', type: 'error' })
        },
        onFinish: () => { isSubmitting.value = false },
    })
}
</script>

<template>
    <Dialog
        :visible="visible"
        @update:visible="emit('update:visible', $event)"
        header="Buat Order"
        modal
        :style="{ width: '500px' }"
        :draggable="false"
    >
        <div class="flex flex-col gap-4 pt-2">

            <!-- Type selector -->
            <div>
                <label class="text-sm font-medium text-gray-700 mb-1.5 block">Jenis Produk <span class="text-red-500">*</span></label>
                <div class="grid grid-cols-2 gap-2">
                    <button
                        type="button"
                        @click="selectedType = 'seng'"
                        class="flex items-center justify-center gap-2 rounded-full border-2 px-3 py-2.5 text-sm font-semibold transition"
                        :class="selectedType === 'seng'
                            ? 'border-amber-400 bg-amber-50 text-amber-700'
                            : 'border-gray-200 bg-white text-gray-500 hover:border-amber-300'"
                    >
                        <FontAwesomeIcon icon="fal fa-box-open" fixed-width aria-hidden="true" />
                        Seng
                    </button>
                    <button
                        type="button"
                        @click="selectedType = 'flexo'"
                        class="flex items-center justify-center gap-2 rounded-full border-2 px-3 py-2.5 text-sm font-semibold transition"
                        :class="selectedType === 'flexo'
                            ? 'border-blue-400 bg-blue-50 text-blue-700'
                            : 'border-gray-200 bg-white text-gray-500 hover:border-blue-300'"
                    >
                        <FontAwesomeIcon icon="fal fa-print" fixed-width aria-hidden="true" />
                        Flexo
                    </button>
                </div>
            </div>

            <!-- Pelanggan -->
            <div>
                <label class="text-sm font-medium text-gray-700 mb-1 block">Pelanggan <span class="text-red-500">*</span></label>
                <Select
                    v-model="customerId"
                    :options="customers"
                    optionLabel="name"
                    optionValue="id"
                    placeholder="Pilih pelanggan"
                    class="w-full"
                    filter
                >
                    <template #value="{ value, placeholder }">
                        <template v-if="value">
                            <div class="flex items-center gap-2">
                                <span>{{ customers.find(c => c.id === value)?.name }}</span>
                                <span class="text-xs text-gray-400">{{ customers.find(c => c.id === value)?.orders_count }} order</span>
                            </div>
                        </template>
                        <template v-else>
                            <span class="text-gray-400">{{ placeholder }}</span>
                        </template>
                    </template>
                    <template #option="{ option }">
                        <div class="flex items-center gap-2">
                            <span>{{ option.name }}</span>
                            <span class="ml-auto text-xs text-gray-400">{{ option.orders_count }} order</span>
                        </div>
                    </template>
                </Select>
            </div>

            <!-- Seng: pilih produk -->
            <template v-if="selectedType === 'seng'">
                <div>
                    <label class="text-sm font-medium text-gray-700 mb-1 block">Produk Seng <span class="text-red-500">*</span></label>
                    <Select
                        v-model="productId"
                        :options="seng_products"
                        optionLabel="name"
                        optionValue="id"
                        placeholder="Pilih produk Seng"
                        class="w-full"
                        filter
                    >
                        <template #value="{ value, placeholder }">
                            <template v-if="value">
                                <div class="flex items-center justify-between w-full gap-3">
                                    <div class="flex items-center gap-1.5">
                                        <span>{{ seng_products.find(p => p.id === value)?.name }}</span>
                                        <span v-if="seng_products.find(p => p.id === value)?.is_pinned" v-tooltip="'Disematkan oleh kasir'">
                                            <FontAwesomeIcon icon="fas fa-thumbtack" class="text-amber-500 text-xs" fixed-width aria-hidden="true" />
                                        </span>
                                        <span class="opacity-70 text-sm">
                                            {{ `(${seng_products.find(p => p.id === value)?.quantity} stok)` }}
                                        </span>
                                    </div>
                                    <span class="text-sm text-gray-400">{{ useFormatCurrency(seng_products.find(p => p.id === value)?.price ?? 0) }}</span>
                                </div>
                            </template>
                            <template v-else>
                                <span class="text-gray-400">{{ placeholder }}</span>
                            </template>
                        </template>
                        <template #option="{ option }">
                            <div class="flex items-center justify-between w-full gap-3">
                                <div class="flex items-center gap-1.5">
                                    <span>{{ option.name }}</span>
                                    <span v-if="option.is_pinned" v-tooltip="'Disematkan oleh kasir'">
                                        <FontAwesomeIcon icon="fas fa-thumbtack" class="text-amber-500 text-xs" fixed-width aria-hidden="true" />
                                    </span>
                                    <span class="opacity-70 text-sm">
                                        {{ `(${option.quantity} stok)` }}
                                    </span>
                                </div>
                                <span class="text-sm text-gray-400">{{ useFormatCurrency(option.price) }}</span>
                            </div>
                        </template>
                    </Select>
                </div>
            </template>

            <!-- Flexo: dimensi -->
            <template v-else>
                <div v-if="!flexo_product" class="rounded-lg border border-yellow-300 bg-yellow-50 px-3 py-2 text-sm text-yellow-700">
                    Produk Flexo belum tersedia.
                </div>
                <template v-else>
                    <div class="grid grid-cols-2 gap-3">
                        <div>
                            <label class="text-sm font-medium text-gray-700 mb-1 block">Panjang (cm) <span class="text-red-500">*</span></label>
                            <InputNumber v-model="dimHeight" placeholder="cm" :minFractionDigits="0" :maxFractionDigits="2" :min="0.1" fluid />
                        </div>
                        <div>
                            <label class="text-sm font-medium text-gray-700 mb-1 block">Lebar (cm) <span class="text-red-500">*</span></label>
                            <InputNumber v-model="dimWidth" placeholder="cm" :minFractionDigits="0" :maxFractionDigits="2" :min="0.1" fluid />
                        </div>
                    </div>
                    <div class="text-xs text-gray-500">
                        Harga per unit: <span class="font-semibold">{{ useFormatCurrency(flexoUnitPrice) }}/cm²</span>
                        <span v-if="customerId && flexo_customer_prices[customerId]" class="text-blue-500 ml-1">(harga khusus)</span>
                    </div>
                </template>
            </template>

            <!-- Jumlah -->
            <div>
                <label class="text-sm font-medium text-gray-700 mb-1 block">Jumlah <span class="text-red-500">*</span></label>
                <InputNumber v-model="quantity" :min="1" :max="999" showButtons class="w-full" />
            </div>

            <!-- No. Order -->
            <!-- <div>
                <label class="text-sm font-medium text-gray-700 mb-1 block">Nomor Order <span class="text-gray-400">(opsional)</span></label>
                <InputText v-model="reference" class="w-full" placeholder="Otomatis dibuat jika kosong" />
            </div> -->

            <!-- File Merek -->
            <div>
                <label class="text-sm font-medium text-gray-700 mb-1 block">
                    File merek <span class="text-gray-400">(opsional)</span>
                    <InformationIcon class="ml-1" :information="'Akan ditampilkan di Manufacture, Invoice, dll'" />
                </label>
                <InputText v-model="fileMerek" class="w-full" placeholder="File merek produk..." />
            </div>

            <!-- Catatan -->
            <div>
                <label class="text-sm font-medium text-gray-700 mb-1 block">
                    Catatan <span class="text-gray-400">(opsional)</span>
                    <InformationIcon class="ml-1" :information="'Catatan tambahan untuk order. Untuk sesama staff. Tidak ditampilkan ke customer.'" />
                </label>
                <Textarea v-model="note" rows="2" class="w-full" placeholder="Catatan tambahan..." />
            </div>

            <!-- Preview total -->
            <div class="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
                <div v-if="selectedType === 'flexo' && flexo_product" class="text-xs text-gray-500 mb-0.5">
                    {{ quantity }} pcs × {{ dimHeight || '?' }} cm × {{ dimWidth || '?' }} cm × {{ useFormatCurrency(flexoUnitPrice) }}
                </div>
                <div v-else-if="selectedType === 'seng' && productId" class="text-xs text-gray-500 mb-0.5">
                    {{ quantity }} pcs × {{ useFormatCurrency(seng_products.find(p => p.id === productId)?.price ?? 0) }}
                </div>
                <div class="font-bold text-gray-800">
                    Total: {{ useFormatCurrency(selectedType === 'flexo' ? flexoTotal : sengTotal) }}
                </div>
            </div>
        </div>

        <template #footer>
            <Button @click="close" type="tertiary" class="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Batal</Button>
            <Button
                @click="submit"
                :disabled="selectedType === 'flexo' && !flexo_product || (selectedType === 'flexo' && (!dimWidth || !dimHeight)) || (selectedType === 'seng' && !productId)"
                :loading="isSubmitting"
                xclass="px-5 py-2 rounded-lg text-sm font-medium text-white bg-sky-500 hover:bg-sky-600 transition disabled:opacity-50"
                full
                icon="fas fa-plus"
                label="Buat Order baru"
            />
        </template>
    </Dialog>
</template>